import { Select, Button, Group, Tooltip } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { UserShortEntityDto } from 'api/dtos/user';
import { useStorage } from 'hooks/useStorage';
import useGetCurrentEntityId, { notifyCurrentEntityIdChanged } from 'hooks/useGetCurrentEntityId';
import { CURRENT_ENTITY_ID } from 'hooks/keys';
import useGetUser from 'hooks/useGetUser';
import { ChurchLogo } from 'Components/Common/icons/ChurchLogo';
import styles from './EntitySelection.module.scss';
import { useMainContainer } from 'Components/Common/Container/context/ContainerContext';
import { accountApi } from 'api/accountEndpoints';
import { AppDispatch } from 'store/store';

const EntitySelection = () => {
    const intl = useIntl();
    const user = useGetUser();
    const currentEntityId = useGetCurrentEntityId();
    const dispatch = useDispatch<AppDispatch>();
    const {
        topBarAction,
        topBarActionTitle,
        topBarIcon,
        disabledAction,
        disabledActionTooltip,
    } = useMainContainer();
    const isButtonVisible = topBarAction !== null && topBarActionTitle !== undefined;

    const {
        specifiedSetItem,
    } = useStorage('local', user?.id ?? '');

    const getEntityTypeLabel = (item: UserShortEntityDto) => {
        if (item.isParish) {
            return intl.formatMessage(messages.parish);
        }

        if (item.isIndependent) {
            return intl.formatMessage(messages.independent);
        }

        return intl.formatMessage(messages.parishGroup);
    };

    const selectData = (user?.entities ?? []).map((item: UserShortEntityDto) => ({
        value: item.id,
        label: item.parentName
            ? `${item.name} (${item.parentName}) - ${getEntityTypeLabel(item)}`
            : `${item.name} - ${getEntityTypeLabel(item)}`,
    }));

    const onChange = (value: string | null) => {
        if (value && value !== currentEntityId && user) {
            specifiedSetItem(CURRENT_ENTITY_ID, value);
            notifyCurrentEntityIdChanged(user.id, value);
            dispatch(accountApi.util.resetApiState());
            dispatch(accountApi.util.upsertQueryData('getUserData', undefined, user))
                .then(() => undefined, () => undefined);
        }
    };

    return (
        <Group gap="md" align="center">
            <span className={styles.title}>
                {intl.formatMessage(messages.selectedEntity)}
            </span>

            <Select
                data={selectData}
                value={currentEntityId}
                onChange={onChange}
                placeholder={intl.formatMessage(messages.placeholder)}
                size="md"
                leftSection={<ChurchLogo size={18} />}
                allowDeselect={false}
                searchable
                styles={{
                    root: { width: 280 },
                }}
            />

            {isButtonVisible && (() => {
                const actionButton = (
                    <Button
                        size="md"
                        leftSection={topBarIcon}
                        className={styles.button}
                        disabled={disabledAction}
                        onClick={() => {
                            if (topBarAction) {
                                topBarAction();
                            }
                        }}
                    >
                        <span className={styles.buttonText}>{topBarActionTitle}</span>
                    </Button>
                );

                if (!disabledAction || !disabledActionTooltip) {
                    return actionButton;
                }

                return (
                    <Tooltip label={disabledActionTooltip} withArrow multiline w={320}>
                        <span>{actionButton}</span>
                    </Tooltip>
                );
            })()}
        </Group>
    );
};

const messages = defineMessages({
    selectedEntity: {
        id: 'src.Components.TopBar.EntitySelection.selectedEntity',
        defaultMessage: 'Wybrana grupa:',
    },
    titleText: {
        id: 'src.Components.TopBar.EntitySelection.titleText',
        defaultMessage: 'Wybierz grupę',
    },
    placeholder: {
        id: 'src.Components.TopBar.EntitySelection.placeholder',
        defaultMessage: 'Brak dostępnych grup',
    },
    parish: {
        id: 'src.Components.TopBar.EntitySelection.parish',
        defaultMessage: 'parafia',
    },
    independent: {
        id: 'src.Components.TopBar.EntitySelection.independent',
        defaultMessage: 'niezależna',
    },
    parishGroup: {
        id: 'src.Components.TopBar.EntitySelection.parishGroup',
        defaultMessage: 'parafialna',
    },
});

export default EntitySelection;
